// My Surveys (participant) — the open surveys an employee was invited to, and the form to answer
// one. Anonymous surveys show a clear privacy notice; answers post to /performance/my/surveys and
// are keyed server-side by an opaque token, never by the employee's identity.
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Loader2, Lock, CheckCircle2, ChevronLeft } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { getApiErrorMessage } from '@/lib/apiError';
import * as api from '@/services/performanceService';

export const MySurveys: React.FC = () => {
  const [list, setList] = useState<api.MySurvey[]>([]);
  const [loading, setLoading] = useState(true);
  const [taking, setTaking] = useState<api.Survey | null>(null);
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [busy, setBusy] = useState(false);

  const load = () => api.listMySurveys().then(setList)
    .catch((e) => toast.error(getApiErrorMessage(e, 'Could not load your surveys')))
    .finally(() => setLoading(false));
  useEffect(() => { void load(); }, []);

  const open = async (id: string) => {
    try { const s = await api.getSurveyToTake(id); setTaking(s); setAnswers({}); }
    catch (e) { toast.error(getApiErrorMessage(e, 'Could not open the survey')); }
  };

  const submit = async () => {
    if (!taking) return;
    const payload = (taking.questions || [])
      .filter((q) => answers[q.id!] !== undefined && answers[q.id!] !== '')
      .map((q) => ({ question_id: q.id!, value: answers[q.id!] }));
    setBusy(true);
    try {
      await api.submitSurvey(taking.id, payload);
      toast.success(taking.identity_mode !== 'NAMED' ? 'Recorded anonymously — thank you' : 'Response recorded — thank you');
      setTaking(null); void load();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Could not submit your response')); }
    finally { setBusy(false); }
  };

  const setA = (qid: string, v: unknown) => setAnswers((p) => ({ ...p, [qid]: v }));

  if (taking) {
    const anon = taking.identity_mode !== 'NAMED';
    return (
      <div className="max-w-2xl">
        <button onClick={() => setTaking(null)} className="mb-3 flex items-center gap-1 text-sm text-primary-600 hover:underline"><ChevronLeft size={16} /> Back</button>
        <h1 className="text-2xl font-bold text-charcoal">{taking.title}</h1>
        {taking.description && <p style={{ color: '#64748b', marginTop: 4 }}>{taking.description}</p>}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: anon ? '#059669' : '#64748b', margin: '10px 0 20px' }}>
          <Lock size={14} /> {anon ? 'Your responses are anonymous — HR sees aggregate results only, once enough people respond.' : 'This survey is not anonymous; your name is attached to your answers.'}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {(taking.questions || []).map((q, i) => {
            const cfg = (q.config || {}) as { options?: string[]; scale_max?: number };
            const qid = q.id!;
            return (
              <Card key={qid}>
                <CardContent style={{ padding: 16 }}>
                  <div style={{ fontWeight: 600, marginBottom: 10 }}>{i + 1}. {q.prompt}{q.required && <span style={{ color: '#ef4444' }}> *</span>}</div>
                  {(q.type === 'single' || q.type === 'yesno') && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {(q.type === 'yesno' ? ['Yes', 'No'] : cfg.options || []).map((o) => (
                        <label key={o} style={optRow}><input type="radio" name={qid} checked={answers[qid] === o} onChange={() => setA(qid, o)} /> {o}</label>
                      ))}
                    </div>
                  )}
                  {q.type === 'multi' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {(cfg.options || []).map((o) => {
                        const arr = (answers[qid] as string[]) || [];
                        return <label key={o} style={optRow}><input type="checkbox" checked={arr.includes(o)}
                          onChange={(e) => setA(qid, e.target.checked ? [...arr, o] : arr.filter((x) => x !== o))} /> {o}</label>;
                      })}
                    </div>
                  )}
                  {(q.type === 'rating' || q.type === 'nps') && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {Array.from({ length: q.type === 'nps' ? 11 : (cfg.scale_max || 5) }, (_, n) => (q.type === 'nps' ? n : n + 1)).map((n) => (
                        <button key={n} onClick={() => setA(qid, n)} style={{ width: 40, height: 40, borderRadius: 8, border: '1px solid #cbd5e1', cursor: 'pointer', background: answers[qid] === n ? '#6366f1' : '#fff', color: answers[qid] === n ? '#fff' : '#334155' }}>{n}</button>
                      ))}
                    </div>
                  )}
                  {q.type === 'numeric' && <input type="number" value={(answers[qid] as number) ?? ''} onChange={(e) => setA(qid, e.target.value === '' ? '' : Number(e.target.value))} style={inp} />}
                  {(q.type === 'short_text' || q.type === 'long_text') && (
                    <textarea value={(answers[qid] as string) || ''} onChange={(e) => setA(qid, e.target.value)} style={{ ...inp, minHeight: q.type === 'long_text' ? 90 : 44 }} placeholder="Your answer" />
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
        {anon && <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 12 }}>Tip: avoid writing anything in free-text that could identify you.</p>}
        <Button disabled={busy} onClick={submit} style={{ marginTop: 16 }}>{busy ? <Loader2 className="animate-spin" size={15} /> : 'Submit response'}</Button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-charcoal">My Surveys</h1>
        <p className="mt-1 text-sm text-muted-foreground">Surveys you've been invited to.</p>
      </div>
      {loading ? <Loader2 className="animate-spin text-muted-foreground" /> : list.length === 0 ? (
        <p className="text-sm text-muted-foreground">No open surveys right now.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {list.map((s) => (
            <Card key={s.id}>
              <CardContent style={{ padding: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
                    {s.title}
                    {s.anonymous && <span style={{ fontSize: 11, color: '#059669', display: 'inline-flex', alignItems: 'center', gap: 3 }}><Lock size={11} /> Anonymous</span>}
                  </div>
                  {s.description && <p style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{s.description}</p>}
                  {s.closes_at && <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>Closes {new Date(s.closes_at).toLocaleDateString()}</p>}
                </div>
                {s.responded ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#059669', fontSize: 13 }}><CheckCircle2 size={16} /> Done</span>
                ) : (
                  <Button onClick={() => open(s.id)}>Take survey</Button>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

const inp: React.CSSProperties = { padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14, width: '100%' };
const optRow: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' };

export default MySurveys;
